import React, { useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import { OnpContext } from './OnpLayout';
import { SelectPaymentModal } from './SelectPaymentModal';
import { useHistory, useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { getItem as getItemUtils, computeTotal } from './utils';
import ConsumptionCard from './ConsumptionCard';
import { notifyError } from '../../util';
import { UserStorage } from './OnpLocalStorage';
import { AskNameModal } from './AskNameModal';
import { ConfirmTableNumberModal } from './ConfirmTableNumberModal';
import { translate } from '../../i18n/customI18nProvider';

export default function Cart() {
    const history = useHistory();

    const storedName = UserStorage.getName();    

    const { barId, table_number } = useParams();

    const { barInfo, order, consumptionChange, clearOrder, getConsumptionQuantity, getConsumptions, navigate } = useContext(OnpContext) || { barInfo: { consumptions: [] } };

    const [paymentModal, setPaymentModal] = useState({open: false, payment: null})
    const [askNameModal, setAskNameModal] = useState({open: false, name: storedName || ""});
    const [confirmTableNumberModal, setConfirmTableNumberModal] = useState({open: false});

    const fmt = (n) => new Intl.NumberFormat('fr-CH', { style: 'currency', currency: 'CHF' }).format(n);

    const getItem = (id) => getItemUtils(barInfo, id);

    const getTotal = () => computeTotal(order, barInfo);

    const selectPaymentMethod = (method) => {
        setPaymentModal({open: false, payment: method});
        if (method === 'twint') {
            navigate('/onp/' + barId + '/' + table_number + '/twint?userName=' + askNameModal.name);
        } else if (method === 'twint_direct') {
            navigate('/onp/' + barId + '/' + table_number + '/twint_direct?userName=' + askNameModal.name);
        } else if (method === 'card') {            
            navigate('/onp/' + barId + '/' + table_number + '/card?userName=' + askNameModal.name);
        } else {
            notifyError(translate('custom.payment_method_not_supported') + method);
        }
    }

    const confirmName = (name, remindName) => {
        if(remindName) {
            UserStorage.setName(name);
        } else {
            UserStorage.setName(null);
        }
        setAskNameModal({open: false, name: name});
        setConfirmTableNumberModal({open: true});
    }

    const confirmTableNumber = (tableNumber) => {
        if(tableNumber !== table_number) {
            navigate('/onp/' + barId + '/' + tableNumber + '/cart');
        }
        setConfirmTableNumberModal({open: false});
        setPaymentModal({open: true, payment: null});
    }

    return (
        <div style={{ flex: 1, overflowY: 'auto', paddingBottom: "300px", scrollbarWidth: 'none', maxHeight: 'calc(100vh - 500px)' }}>
            <Box sx={{ width: '100%', mt: 2 }}>
                <Paper sx={{ p: 2, marginBottom: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>{translate('custom.your_cart')}</Typography>
                <Divider sx={{ mb: 1 }} />

                    <List>
                        {order.length === 0 && (
                        <ListItem>
                            <ListItemText primary={translate('custom.your_cart_is_empty')} />
                        </ListItem>
                        )}

                        {order.map((element) => {
                            const item = getItem(element.id) || {};
                            const deposit = barInfo.allDeposits?.find(d => d._id === item.deposit);
                            const depositPrice = Number(deposit?.price || 0);
                            return(
                                <div key={item._id} style={{ marginBottom: 16}}>
                                    <ConsumptionCard 
                                        key={item._id} 
                                        consumption={item} 
                                        quantity={getConsumptionQuantity(item._id)}
                                        onChange={(quantity) => consumptionChange(item._id, quantity)}
                                        allDeposits={barInfo.allDeposits || []}
                                        withoutCard={true}
                                    />
                                </div>
                            )
                        })}
                </List>
                </Paper>

                <Paper sx={{ p: 2, position: { md: 'sticky' }, top: { md: 96 } }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{translate('custom.summary')}</Typography>
                    <Divider sx={{ my: 1 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography>{translate('custom.subtotal')}</Typography>
                        <Typography sx={{ fontWeight: 700 }}>{fmt(getTotal())}</Typography>
                    </Box>

                    <Stack spacing={3}>
                        <Button size="large" color="success" variant="contained" onClick={() => {setAskNameModal({open: true, name: storedName || ""})}} disabled={order.length === 0}>{translate('custom.pay')}</Button>
                        <Button size="large" color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={clearOrder} disabled={order.length === 0}>{translate('custom.empty_cart')}</Button>
                    </Stack>
                </Paper>

                <AskNameModal name={storedName || ""} open={askNameModal.open} onClose={() => setAskNameModal({open: false, name: askNameModal.name})} onConfirm={confirmName} />
                <ConfirmTableNumberModal tableNumber={table_number} open={confirmTableNumberModal.open} onClose={() => setConfirmTableNumberModal({open: false})} onConfirm={confirmTableNumber} />
                <SelectPaymentModal methods={barInfo.paymentMethods} open={paymentModal.open} onClose={() => setPaymentModal({open: false, payment: null})} onSelect={selectPaymentMethod} />
            </Box>
        </div>
    );
}